import { useState } from "react";
import { AlertCircle, Loader2, UserCog, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MeetingResponsibleSelect } from "@/components/client360/MeetingResponsibleSelect";
import { useReassignMeeting } from "@/hooks/useClient360Meetings";

/**
 * S4 — reassign a meeting's responsible (D-068). Picker = own/team-scoped
 * (ActionScopeResolver); reason is MANDATORY (empty → blocked, backend 422). On
 * success the backend writes a Timeline-event (source=supervisor_reassign).
 */
export function ReassignResponsibleDrawer({
  interactionId,
  holdingId,
  onClose,
}: {
  interactionId: string;
  holdingId: string;
  onClose: () => void;
}) {
  const mutation = useReassignMeeting(holdingId);
  const [assigneeId, setAssigneeId] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const canSubmit = assigneeId !== "" && reason.trim() !== "" && !mutation.isPending;

  const submit = async () => {
    if (reason.trim() === "") { setErr("Вкажіть причину перепризначення."); return; }
    try {
      await mutation.mutateAsync({ interactionId, assignedUserId: assigneeId, reason: reason.trim() });
      onClose();
    } catch (e) {
      const status = (e as { status?: number } | null)?.status;
      if (status === 409) setErr("Відповідальний більше не доступний для призначення. Список оновлено.");
      else if (status === 403) setErr("Перепризначення поза вашою зоною доступу.");
      else setErr(e instanceof Error ? e.message : "Не вдалося перепризначити.");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full sm:w-[420px] overflow-y-auto bg-background shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <UserCog className="h-4 w-4 text-primary" aria-hidden /> Перепризначити відповідального
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Новий відповідальний</label>
            <MeetingResponsibleSelect holdingId={holdingId} value={assigneeId} onChange={setAssigneeId} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Причина <span className="text-destructive">*</span>
            </label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="напр. менеджер у відпустці" />
          </div>
          {err && (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {err}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 flex gap-2 border-t bg-background px-5 py-4">
          <Button type="button" onClick={submit} disabled={!canSubmit} className="h-11 flex-1 text-sm font-semibold">
            {mutation.isPending ? (<><Loader2 className="h-4 w-4 animate-spin" /> Збереження…</>) : "Перепризначити"}
          </Button>
          <Button type="button" variant="outline" onClick={onClose} className="h-11 text-sm">Скасувати</Button>
        </div>
      </div>
    </div>
  );
}
